import { motion, useAnimate, type AnimationSequence } from 'framer-motion'
import { memo, useCallback, useEffect, useState } from 'react'
import type { LiveGiftsAnimations } from '../../../../types/lives/animations'

interface Props {
  animation: LiveGiftsAnimations
  removeAnimation: (id: string) => void
}

const INITIAL_X = window.innerWidth / 2.5
const INITIAL_Y = window.innerHeight / 4
const DURATION = 0.2

export const SubAnimations = memo(
  ({ elementToGo, imgGift }: { elementToGo: string; imgGift: string }) => {
    const [scope, animate] = useAnimate()

    useEffect(() => {
      if (scope.current) {
        const element = document.getElementById(elementToGo)

        if (element) {
          const { x, y } = element.getBoundingClientRect()

          const sequence: AnimationSequence = [
            [scope.current, { x, y }, { duration: DURATION }],
            [scope.current, { opacity: 0 }, { duration: DURATION }],
          ]

          animate(sequence)
        }
      }
    }, [elementToGo, scope, animate])

    return (
      <motion.div
        ref={scope}
        className="absolute w-20 h-20 z-10 opacity-100"
        style={{ x: INITIAL_X, y: INITIAL_Y }}
      >
        <img src={imgGift} className="w-full h-full" />
      </motion.div>
    )
  },
)

export const Animation = memo(({ animation, removeAnimation }: Props) => {
  const [scope, animate] = useAnimate()
  const [subElements, setSubElements] = useState<string[]>([])

  const handleAddSubElements = useCallback(() => {
    const [, ...elements] = animation.elementsToGo

    setSubElements(elements)
  }, [animation])

  const makeAnimation = useCallback(async () => {
    const element = document.getElementById(animation.elementsToGo[0])

    if (element && animation.id) {
      const { x, y } = element.getBoundingClientRect()

      const sequenceStart: AnimationSequence = [
        [scope.current, { opacity: 1 }, { duration: DURATION }],
        [scope.current, { x: INITIAL_X, y: INITIAL_Y }, { duration: DURATION }],
      ]

      const sequenceFinish: AnimationSequence = [
        [scope.current, { x, y }, { duration: DURATION }],
        [scope.current, { opacity: 0 }, { duration: DURATION }],
      ]

      await animate(sequenceStart)
      handleAddSubElements()
      await animate(sequenceFinish)
      removeAnimation(animation.id)
    }
  }, [scope, animation, animate, handleAddSubElements, removeAnimation])

  useEffect(() => {
    if (scope.current) {
      makeAnimation()
    }
  }, [animation, scope, animate, makeAnimation])

  return (
    <div>
      <motion.div
        ref={scope}
        className="flex justify-center items-center absolute w-20 h-20 transform opacity-0 z-10"
        style={{ x: INITIAL_X }}
      >
        <img src={animation.imgGift} className="w-full h-full" />
      </motion.div>

      {subElements.map((element) => (
        <SubAnimations
          key={element}
          elementToGo={element}
          imgGift={animation.imgGift}
        />
      ))}
    </div>
  )
})
